import type { PendingEnrolment } from './actions'

export type ConfirmationEnrolment = {
  enrolment_entitlement_id: string;
  participant_id: string;
}

export type ConfirmationRequest = {
  enrolments: ConfirmationEnrolment[];
  unenrolments: string[];
}

export type ConfirmationError = {
  code?: string;
  title?: string;
  detail?: string;
  source?: string;
}

export type SubmittedEnrolmentChange = {
  kind: 'enrolment';
  entitlementId: string;
  participantId: string;
}

export type SubmittedUnenrolmentChange = {
  kind: 'unenrolment';
  enrolmentId: string;
}

export type SubmittedChange =
  | SubmittedEnrolmentChange
  | SubmittedUnenrolmentChange

// An error with the change it was raised against, so the alert can name the
// participant rather than a request index.
export type SubmitError = ConfirmationError & {
  change: SubmittedChange | null;
}

const ENROLMENT_SOURCE = /^enrolments\[(\d+)\](\..+)?$/
const UNENROLMENT_SOURCE = /^unenrolments\[(\d+)\]$/

export const buildConfirmationRequest = (
  enrolments: Record<string, PendingEnrolment>,
  unenrolments: Record<string, true>,
): ConfirmationRequest => ({
  enrolments: Object.values(enrolments).map(
    ({ entitlementId, participantId }) => ({
      enrolment_entitlement_id: entitlementId,
      participant_id: participantId,
    }),
  ),
  unenrolments: Object.keys(unenrolments),
})

const enrolmentChangeAt = (
  request: ConfirmationRequest,
  index: number,
): SubmittedEnrolmentChange | null => {
  const enrolment = request.enrolments[index]
  if (!enrolment) return null

  return {
    kind: 'enrolment',
    entitlementId: enrolment.enrolment_entitlement_id,
    participantId: enrolment.participant_id,
  }
}

const unenrolmentChangeAt = (
  request: ConfirmationRequest,
  index: number,
): SubmittedUnenrolmentChange | null => {
  const enrolmentId = request.unenrolments[index]
  if (enrolmentId === undefined) return null

  return { kind: 'unenrolment', enrolmentId }
}

const changeForSource = (
  request: ConfirmationRequest,
  source: unknown,
): SubmittedChange | null => {
  if (typeof source !== 'string') return null

  const enrolmentMatch = source.match(ENROLMENT_SOURCE)
  if (enrolmentMatch) {
    return enrolmentChangeAt(request, Number(enrolmentMatch[1]))
  }

  const unenrolmentMatch = source.match(UNENROLMENT_SOURCE)
  if (unenrolmentMatch) {
    return unenrolmentChangeAt(request, Number(unenrolmentMatch[1]))
  }

  return null
}

export const describeSubmitErrors = (
  request: ConfirmationRequest,
  errors: ConfirmationError[],
): SubmitError[] =>
  errors.map(error => ({
    ...error,
    change: changeForSource(request, error.source),
  }))
